import { supabase } from "@/lib/supabaseClient";
import { compressImage } from "@/lib/compressImage";

const UPLOAD_API_URL = (process.env.REACT_APP_UPLOAD_API_URL || "").replace(/\/$/, "");
const R2_PUBLIC_BASE = (process.env.REACT_APP_R2_PUBLIC_BASE_URL || "").replace(/\/$/, "");

const FOLDERS = {
  officeBearers: "office_bearers",
  eventGallery: "event_gallery",
  mediaGallery: "media_gallery",
  banners: "banners",
  pdfs: "pdfs",
  units: "units",
  unitsPost: "units_post",
  sponsors: "sponsors",
  forum: "forum",
  forumPost: "forum_post",
};

function safeSlug(text) {
  return String(text || "file")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48) || "file";
}

function assertUploadConfigured() {
  if (!UPLOAD_API_URL) {
    throw new Error(
      "Upload API not configured. Set REACT_APP_UPLOAD_API_URL to your Cloudflare Worker URL."
    );
  }
}

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error(
      "Sign in with a confirmed Supabase admin account to upload files. Demo login cannot upload."
    );
  }
  return session.access_token;
}

async function uploadViaWorker(file, folder, nameHint) {
  assertUploadConfigured();
  const token = await getAccessToken();

  const form = new FormData();
  form.append("file", file);
  form.append("folder", folder);
  form.append("nameHint", nameHint);

  const res = await fetch(`${UPLOAD_API_URL}/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });

  let payload = {};
  try {
    payload = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(payload.error || `Upload failed (${res.status})`);
  }

  if (!payload.url) {
    throw new Error("Upload succeeded but no URL was returned");
  }

  return payload.url;
}

async function uploadImage(file, folder, nameHint) {
  const prepared = await compressImage(file);
  return uploadViaWorker(prepared, folder, safeSlug(nameHint) || "image");
}

function r2KeyFromUrl(url) {
  if (!url || !R2_PUBLIC_BASE) return null;
  const normalized = String(url).trim();
  if (!normalized.startsWith(`${R2_PUBLIC_BASE}/`)) return null;
  const key = normalized.slice(R2_PUBLIC_BASE.length + 1);
  return key || null;
}

async function deleteViaWorker(urlOrKey) {
  assertUploadConfigured();
  const key = String(urlOrKey).startsWith("http") ? r2KeyFromUrl(urlOrKey) : String(urlOrKey || "").trim();
  if (!key) return false;

  const token = await getAccessToken();
  const res = await fetch(`${UPLOAD_API_URL}/delete`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ key }),
  });

  let payload = {};
  try {
    payload = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(payload.error || `Delete failed (${res.status})`);
  }

  return true;
}

export const storageService = {
  /** Public R2 base URL (for display / validation). */
  publicBaseUrl: R2_PUBLIC_BASE,

  /** True when the URL points at this project's public R2 bucket. */
  isManagedR2Url(url) {
    return Boolean(r2KeyFromUrl(url));
  },

  /** Delete one managed R2 object by public URL. No-op for external URLs. */
  async deleteR2Url(url) {
    if (!url || !this.isManagedR2Url(url)) return false;
    return deleteViaWorker(url);
  },

  /** Delete multiple managed R2 objects; throws if any deletion fails. */
  async deleteR2Urls(urls = []) {
    const unique = [...new Set(urls.filter((url) => this.isManagedR2Url(url)))];
    if (!unique.length) return;

    const results = await Promise.allSettled(unique.map((url) => this.deleteR2Url(url)));
    const failed = results.filter((result) => result.status === "rejected");
    if (failed.length) {
      const message = failed[0].reason?.message || "Storage delete failed";
      throw new Error(message);
    }
  },

  /**
   * Upload an office bearer photo to R2 (`office_bearers/`).
   * Requires a real Supabase admin session (not demo fallback).
   */
  async uploadOfficeBearerImage(file, nameHint = "bearer") {
    return uploadImage(file, FOLDERS.officeBearers, nameHint);
  },

  /** Upload an event gallery image to R2 (`event_gallery/`). */
  async uploadEventGalleryImage(file, nameHint = "event") {
    return uploadImage(file, FOLDERS.eventGallery, nameHint);
  },

  /** Upload a site media image to R2 (`media_gallery/`). */
  async uploadMediaGalleryImage(file, nameHint = "media") {
    return uploadImage(file, FOLDERS.mediaGallery, nameHint);
  },

  /** Upload a banner image to R2 (`banners/`). */
  async uploadBannerImage(file, nameHint = "banner") {
    return uploadImage(file, FOLDERS.banners, nameHint);
  },

  /** Upload a PDF to R2 (`pdfs/`). No image compression. */
  async uploadPdf(file, nameHint = "document") {
    return uploadViaWorker(file, FOLDERS.pdfs, safeSlug(nameHint) || "document");
  },

  /** Upload a unit image to R2 (`units/`). */
  async uploadUnitImage(file, nameHint = "unit") {
    return uploadImage(file, FOLDERS.units, nameHint);
  },

  /** Upload a unit post image to R2 (`units_post/`). */
  async uploadUnitPostImage(file, nameHint = "unit-post") {
    return uploadImage(file, FOLDERS.unitsPost, nameHint);
  },

  /** Upload a sponsor logo to R2 (`sponsors/`). */
  async uploadSponsorLogo(file, nameHint = "sponsor") {
    return uploadImage(file, FOLDERS.sponsors, nameHint);
  },

  /** Upload a forum hero image to R2 (`forum/`). */
  async uploadForumImage(file, nameHint = "forum") {
    return uploadImage(file, FOLDERS.forum, nameHint);
  },

  /** Upload a forum post image to R2 (`forum_post/`). */
  async uploadForumPostImage(file, nameHint = "forum-post") {
    return uploadImage(file, FOLDERS.forumPost, nameHint);
  },
};
